import { PRIMARY_BUTTON } from "@/constants";
import clsx from "clsx";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { useInventoryVendorListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryVendorListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useEffect, useState } from "react";
import { FaProductHunt } from "react-icons/fa6";
import { Link } from "react-router-dom";

const tableHeader = [{ item: "SN" }, { item: "Vendor Name" }, { item: "Status" }, { item: "Action" }];

const VendorsListComponent = () => {
  const { getRouteKey } = useRouter();
  const { NO_PWL_RESULTS } = useImageConstants();
  const { vendorList, execute: fetchVendorsList } = useInventoryVendorListAPI();
  const isVendorListInProgess = useLoader("isInventoryVendorListLoadingInProgess");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    fetchVendorsList(searchQuery);
  }, [searchQuery]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-10">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">Vendors</h1>
          <p className="mt-2 text-sm text-gray-700 hidden sm:block">A list of all the vendors for the inventories.</p>
        </div>
        <Link to={getRouteKey("VENDOR_ADD", "url")} className={clsx(PRIMARY_BUTTON, "shrink-0")}>
          Add Vendor
        </Link>
      </div>
      <div className="mt-3">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => setSearchQuery(query)}
          containerClassName="w-full sm:w-1/2 lg:w-1/3"
          addon={<FaProductHunt className="w-4 h-4" />}
          placeholder="Search Vendor"
        />
      </div>
      <div className="mt-8 flow-root">
        {isVendorListInProgess ? (
          <TableSkeleton />
        ) : vendorList?.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="No Vendors Onboarded"
            description="No vendors have been onboarded, please onboard some vendors through add vendor."
          />
        ) : (
          <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[600px]">
              <div className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          {tableHeader?.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                            >
                              {key?.item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {vendorList.map((vendor, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === vendorList.length - 1;
                          const isSingle = vendorList.length === 1;

                          return (
                            <tr
                              key={vendor?.vendor_id}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                {idx + 1}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">{vendor?.trade_name}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <ToggleWithIcon forced toggleState={vendor?.status} />
                              </td>
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <Link
                                  to={`${getRouteKey("VENDOR_ADD", "url")}?vendor_id=${vendor.vendor_id}`}
                                  className="underline text-xs text-black"
                                >
                                  Edit<span className="sr-only">, {vendor.trade_name}</span>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorsListComponent;
